import * as Sentry from '@sentry/nuxt'

/**
 * Display-only cache of the last *labeled* device list — separate from `devicePreferences.ts`,
 * which only remembers the user's chosen device. This exists purely so pickers aren't empty
 * before this session's own permission/label unlock has happened (see `manager.ts`'s
 * `initializeDevices`): on a fresh launch where the durable grant hasn't been re-confirmed yet,
 * `enumerateDevices()` can return an empty array for a kind entirely rather than unlabeled
 * entries, and without a fallback the settings page shows no devices at all until the user
 * clicks "Обновить". It is never the source of truth for restoring a selection — that's still
 * always resolved against the live WebRTC list, see `resolveDevicePreference`.
 */
export interface CachedDeviceInfo {
  deviceId: string
  groupId: string
  label: string
  kind: MediaDeviceKind
}

interface DeviceListCache {
  microphones: CachedDeviceInfo[]
  cameras: CachedDeviceInfo[]
  speakers: CachedDeviceInfo[]
}

const STORAGE_KEY = 'iopta:device-list-cache'
const EMPTY_CACHE: DeviceListCache = { microphones: [], cameras: [], speakers: [] }

function canUseLocalStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function toCached(devices: MediaDeviceInfo[]): CachedDeviceInfo[] {
  return devices.map(d => ({ deviceId: d.deviceId, groupId: d.groupId, label: d.label, kind: d.kind }))
}

export function loadDeviceListCache(): DeviceListCache {
  if (!canUseLocalStorage()) return EMPTY_CACHE
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return EMPTY_CACHE
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return EMPTY_CACHE
    return {
      microphones: Array.isArray(parsed.microphones) ? parsed.microphones : [],
      cameras: Array.isArray(parsed.cameras) ? parsed.cameras : [],
      speakers: Array.isArray(parsed.speakers) ? parsed.speakers : [],
    }
  } catch (e) {
    console.warn('[DeviceListCache] Failed to read cached device list:', e)
    Sentry.captureException(e, { level: 'warning', extra: { action: 'read_device_list_cache' } })
    return EMPTY_CACHE
  }
}

/** Call with a device list that actually has real labels — never with a blank/placeholder one. */
export function saveDeviceListCache(microphones: MediaDeviceInfo[], cameras: MediaDeviceInfo[], speakers: MediaDeviceInfo[]) {
  if (!canUseLocalStorage()) return
  try {
    const cache: DeviceListCache = {
      microphones: toCached(microphones),
      cameras: toCached(cameras),
      speakers: toCached(speakers),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache))
  } catch (e) {
    console.warn('[DeviceListCache] Failed to save device list cache:', e)
    Sentry.captureException(e, { level: 'warning', extra: { action: 'save_device_list_cache' } })
  }
}

export function hasLabels(devices: MediaDeviceInfo[]): boolean {
  return devices.some(d => d.label !== '')
}
